(() => {
  if (!('IntersectionObserver' in window) || !window.matchMedia) return;

  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const imageSelector = '.softcore-campaign__scene, .softcore-hero__figure, .softcore-collection-nav__portrait';
  const signatureSelector = '.sc-signature__word';
  const selector = [
    imageSelector,
    signatureSelector,
    '.softcore-campaign__copy',
    '.softcore-departments__title',
    '.softcore-departments__introduction',
    '.softcore-departments__card',
    '.softcore-journal__masthead-inner',
    '.softcore-journal__article',
    '.sc-signature__line',
    '.softcore-collection-nav__copy',
    '.softcore-hero__title',
    '.softcore-notes__header',
    '.softcore-notes__note',
    '.softcore-support__header',
    '.softcore-about__opening',
    '.softcore-about__content > h2',
    '.sc-art-index__header',
    '.sc-art-index__card',
    '.sc-artwork__product',
    '.sc-dept--closing .sc-dept__title',
    '.sc-dept--closing .sc-dept__figure',
    '.sc-dept__invitation',
    '.sc-art-gallery__card',
    '.sc-art-gallery__closing',
    '.softcore-product-story__visual',
    '.softcore-product-story__copy',
    '.sc-living__rooms-heading',
    '.sc-living__card',
    '.sc-living__manifesto h2'
  ].join(', ');
  const classes = ['sc-motion-reveal', 'sc-motion-image', 'sc-motion-signature', 'sc-motion-enter'];
  const completed = new WeakSet();
  const pending = new Set();
  const active = new Set();

  const reset = (element) => {
    element.classList.remove(...classes);
    element.style.removeProperty('--sc-motion-delay');
    active.delete(element);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting || !pending.has(target)) return;
      observer.unobserve(target);
      pending.delete(target);
      completed.add(target);
      if (preference.matches || target.contains(document.activeElement)) {
        reset(target);
        return;
      }
      target.classList.add('sc-motion-enter');
      active.add(target);
    });
  }, { rootMargin: '0px 0px 24px 0px', threshold: 0 });

  const initialize = (root = document) => {
    if (preference.matches) return;
    const targets = Array.from(root.querySelectorAll(selector));
    if (root instanceof Element && root.matches(selector)) targets.unshift(root);
    const siblings = new WeakMap();
    targets.forEach((target) => {
      if (completed.has(target) || pending.has(target) || active.has(target)) return;
      if (target.getBoundingClientRect().top < window.innerHeight || target.contains(document.activeElement)) {
        completed.add(target);
        return;
      }
      const parent = target.parentElement;
      const order = parent ? (siblings.get(parent) || 0) : 0;
      if (parent) siblings.set(parent, order + 1);
      target.style.setProperty('--sc-motion-delay', `${Math.min(order * 80, 240)}ms`);
      target.classList.add('sc-motion-reveal');
      if (target.matches(imageSelector)) target.classList.add('sc-motion-image');
      if (target.matches(signatureSelector)) target.classList.add('sc-motion-signature');
      pending.add(target);
      observer.observe(target);
    });
  };

  const clearSection = (root) => {
    [...pending, ...active].forEach((target) => {
      if (target !== root && !root.contains(target)) return;
      observer.unobserve(target);
      pending.delete(target);
      reset(target);
    });
  };

  preference.addEventListener('change', () => {
    if (!preference.matches) {
      initialize();
      return;
    }
    observer.disconnect();
    [...pending, ...active].forEach(reset);
    pending.clear();
  });

  document.addEventListener('focusin', ({ target }) => {
    const element = target instanceof Element ? target.closest('.sc-motion-reveal') : null;
    if (!element) return;
    observer.unobserve(element);
    pending.delete(element);
    completed.add(element);
    reset(element);
  });

  document.addEventListener('animationend', ({ target, animationName }) => {
    if (animationName.startsWith('sc-motion-') && active.has(target)) reset(target);
  });

  document.addEventListener('shopify:section:load', ({ target }) => initialize(target));
  document.addEventListener('shopify:section:unload', ({ target }) => clearSection(target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();


